"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import { motion, AnimatePresence } from "framer-motion";
import { Vehicle } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import {
  FICHE_UI,
  localizeColor,
  localizeEngine,
  localizeFeatures,
} from "./ficheUi";
import { tr } from "@/config/localeText";

const SectionHeader = ({ locale }: { locale: Locale }) => {
  const t = tr(FICHE_UI, locale).apercu;
  return (
    <div className="text-center">
      <h2 className="text-2xl font-light uppercase text-primaryLoc sm:text-3xl">
        {t.heading}
      </h2>
      <p className="mt-2 text-base text-primaryLoc/80 sm:text-lg">
        {t.subheading}
      </p>
    </div>
  );
};

const VehicleImage = ({ src, alt }: { src: string; alt: string }) => (
  <div className="relative aspect-square w-full">
    <Image src={src} alt={alt} fill className="object-contain" loading="lazy" />
  </div>
);

const HighlightList = ({
  vehicle,
  locale,
}: {
  vehicle: Vehicle;
  locale: Locale;
}) => {
  const t = tr(FICHE_UI, locale).apercu;
  const highlights = [
    { label: t.engine, value: localizeEngine(vehicle.specs.engine, locale) },
    { label: t.powerHp, value: `${vehicle.specs.powerHP}ch` },
    { label: t.powerCv, value: `${vehicle.specs.powerCV}cv` },
    { label: t.exteriorColor, value: vehicle.specs.exteriorColor },
    {
      label: t.interiorColor,
      value: localizeColor(vehicle.specs.interiorColor, locale),
    },
  ];

  return (
    <div className="space-y-6">
      {highlights.map((item, index) => (
        <div key={index} className="text-center">
          <p className="text-lg font-medium text-primaryLoc">{item.value}</p>
          <p className="mt-1 text-sm text-primaryLoc/60">{item.label}</p>
        </div>
      ))}
    </div>
  );
};

const Accordion = ({
  title,
  items,
  isOpen,
  onToggle,
  isDisabled = false,
  isFullWidth = false,
  children,
}: {
  title: string;
  items?: string[];
  isOpen: boolean;
  onToggle: () => void;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  children?: React.ReactNode;
}) => (
  <div
    className={`border-b border-primaryLoc/15 ${isFullWidth ? "w-full" : ""}`}
  >
    <button
      type="button"
      onClick={onToggle}
      disabled={isDisabled}
      className={`flex w-full items-center justify-between py-4 text-start transition-colors ${
        isDisabled ? "cursor-not-allowed opacity-50" : "hover:text-primaryLoc"
      }`}
    >
      <span className="font-medium">{title}</span>
      <Icon
        icon={isOpen ? "mdi:minus" : "mdi:plus"}
        className={`h-5 w-5 transition-transform ${isOpen ? "rotate-0" : "rotate-0"}`}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="pb-4">
            {items ? (
              <ul className="space-y-2 text-sm text-primaryLoc/75">
                {items.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Icon
                      icon="mdi:check"
                      className="h-4 w-4 text-primaryLoc"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              children
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function ApercuDetailsCar({
  vehicle,
  locale = "fr",
}: {
  vehicle: Vehicle;
  locale?: Locale;
}) {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const t = tr(FICHE_UI, locale).apercu;

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <section className="bg-white py-14 md:py-20">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <SectionHeader locale={locale} />

        <div className="mt-10 grid gap-8 lg:mt-12 lg:grid-cols-3">
          {/* Colonne gauche - Image */}
          <div>
            <VehicleImage src={vehicle.images.main.src} alt={vehicle.name} />
          </div>

          {/* Colonne centre - Attributs clés */}
          <div>
            <HighlightList vehicle={vehicle} locale={locale} />
          </div>

          {/* Colonne droite - Accordéons */}
          <div className="space-y-2">
            <Accordion
              title={t.exterior}
              items={localizeFeatures(vehicle.features.exterior, locale)}
              isOpen={openSection === "exterior"}
              onToggle={() => toggleSection("exterior")}
            />
            <Accordion
              title={t.interior}
              items={localizeFeatures(vehicle.features.interior, locale)}
              isOpen={openSection === "interior"}
              onToggle={() => toggleSection("interior")}
            />
            <Accordion
              title={t.safety}
              items={localizeFeatures(vehicle.features.safety, locale)}
              isOpen={openSection === "security"}
              onToggle={() => toggleSection("security")}
            />
            <Accordion
              title={t.other}
              items={localizeFeatures(vehicle.features.other, locale)}
              isOpen={openSection === "other"}
              onToggle={() => toggleSection("other")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
